// Reusable athlete-attribute inputs (gender, DOB, weight, experience).
// Used by Profile.tsx (athlete-edit section) and AthleteJoinTeamModal (first-time
// AR creation). Caller owns the form value state + submission; this component
// only renders inputs and reports changes back through onChange.
//
// The marker `requireAll` controls whether the required fields (gender + DOB)
// get the HTML `required` attribute. Both callers set it. Before, the profile
// form sent no key for an empty field, and the page said "Saved" (#452 review).

import { useI18n } from '../i18n';
import { birthDateMin, todayLocal } from '../utils/calendarDate';
import type { Gender } from '../types';
import Field from './shared/Field';

export interface AthleteFormState {
  gender: string;
  birth_date: string;
  current_weight_kg: string;
  experience_bouts: string;
}

export const EMPTY_ATHLETE_FORM: AthleteFormState = {
  gender: '', birth_date: '', current_weight_kg: '', experience_bouts: '',
};

interface Props {
  value: AthleteFormState;
  onChange: (next: AthleteFormState) => void;
  requireAll?: boolean;
  disabled?: boolean;
  /** #674 — the lowest bout count that the user can enter. Pass the stored
   *  count when the user cannot lower it (not an admin and not a team
   *  manager). The input gets this `min`. The server decides: it refuses a
   *  lower count with 403, so the `min` is a hint. Leave it out for a new
   *  record and for a user who can lower the count: the `min` is then 0. */
  minExperience?: number;
}

export default function AthleteFormFields({ value, onChange, requireAll, disabled, minExperience }: Props) {
  const { t } = useI18n();
  function patch(p: Partial<AthleteFormState>) { onChange({ ...value, ...p }); }
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t.common.gender} required={requireAll}>
          <select disabled={disabled} className="input" value={value.gender}
            onChange={e => patch({ gender: e.target.value })}>
            <option value="">—</option>
            <option value="Male">{t.common.male}</option>
            <option value="Female">{t.common.female}</option>
          </select>
        </Field>
        <Field label={t.profile.birthDate} required={requireAll}>
          <input type="date" disabled={disabled} className="input"
            min={birthDateMin()} max={todayLocal()}
            value={value.birth_date}
            onChange={e => patch({ birth_date: e.target.value })} />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t.profile.experienceBouts}>
          <input type="number" min={minExperience ?? 0} disabled={disabled} className="input" value={value.experience_bouts}
            data-testid="profile-athlete-experience" onChange={e => patch({ experience_bouts: e.target.value })} />
        </Field>
        <Field label={t.profile.currentWeight}>
          <input type="number" min="0" step="0.1" disabled={disabled} className="input" value={value.current_weight_kg}
            data-testid="profile-athlete-weight" onChange={e => patch({ current_weight_kg: e.target.value })} />
        </Field>
      </div>
    </>
  );
}

/** The form state of a stored athlete record (the load, and the saved row
 *  after a save). */
export function athleteFormFrom(ar: {
  gender?: string | null; birth_date?: string | null;
  experience_bouts?: number | null; current_weight_kg?: number | string | null;
}): AthleteFormState {
  return {
    gender: ar.gender || '',
    birth_date: ar.birth_date ? ar.birth_date.split('T')[0] : '',
    experience_bouts: ar.experience_bouts?.toString() || '',
    current_weight_kg: ar.current_weight_kg?.toString() || '',
  };
}

type Counters = Pick<AthleteFormState, 'experience_bouts' | 'current_weight_kg'>;

/** #627 — the experience and the weight go in the PATCH only when the user
 *  changed them from the loaded values. Other flows write the two numbers
 *  while a form is open: a verdict adds 1 to the experience, and a weigh-in
 *  sets the weight. A form that sends the loaded value writes the old value
 *  back, and the server copies an old experience into the open registrations
 *  (#618). The compare uses the parsed numbers, so "70" and "70.0" are one
 *  weight. A cleared weight goes as null, never undefined (#452). */
export function changedCounters(form: Counters, loaded: Counters): {
  experience_bouts?: number; current_weight_kg?: number | null;
} {
  const bouts = (s: string) => (s ? parseInt(s) : 0);
  const kg = (s: string) => (s ? parseFloat(s) : null);
  const out: { experience_bouts?: number; current_weight_kg?: number | null } = {};
  if (bouts(form.experience_bouts) !== bouts(loaded.experience_bouts)) out.experience_bouts = bouts(form.experience_bouts);
  if (kg(form.current_weight_kg) !== kg(loaded.current_weight_kg)) out.current_weight_kg = kg(form.current_weight_kg);
  return out;
}

/** Convert the form-state strings to the API request shape. `loaded` is the
 *  form state of the stored record: the experience and the weight go only
 *  when they differ from it (`changedCounters`, #627). Gender and birth date
 *  are required (`requireAll`), so the HTML `required` stops a submit with
 *  an empty one (#452). Gender narrowing: the <select> only emits
 *  ''/'Male'/'Female', so the cast is sound. */
export function toAthletePayload(form: AthleteFormState, loaded: AthleteFormState): {
  gender?: Gender; birth_date?: string;
  current_weight_kg?: number | null; experience_bouts?: number;
} {
  return {
    gender: (form.gender || undefined) as Gender | undefined,
    birth_date: form.birth_date || undefined,
    ...changedCounters(form, loaded),
  };
}
